import { useEffect, useRef, useState } from "react";

import { useAtomValue } from "../app-jotai";
import { isRealUser, meAtom, meLoadedAtom } from "../data/me";

// Top-right login state — nothing else. All save/load/open lives in the top-left
// menu. Two states only:
//   • real Google user → avatar + first name, opening a small menu (Sign out);
//   • everyone else (anonymous, or the local-dev auth bypass) → a "Sign in with
//     Google" pill, so login is always visible and encouraged.
// (Rendered inside App's renderTopRightUI, already hidden for share-link guests.)

const buttonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: ".4rem",
  height: "2.25rem",
  padding: "0 0.85rem",
  borderRadius: "0.75rem",
  border: "1px solid var(--default-border-color, #e3e2e4)",
  background: "var(--island-bg-color, #fff)",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.875rem",
  whiteSpace: "nowrap",
};

export const AccountChip = ({
  onSignedOut,
}: {
  onSignedOut: () => void | Promise<void>;
}) => {
  const me = useAtomValue(meAtom);
  const meLoaded = useAtomValue(meLoadedAtom);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  // Avoid a flash of the wrong state before /api/me resolves.
  if (!meLoaded) {
    return null;
  }

  const signOut = async () => {
    setMenuOpen(false);
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {
      // ignore — refresh below reflects whatever the server now reports
    }
    await onSignedOut();
  };

  // Not a real Google account (anonymous, or the local-dev bypass) → invite login.
  if (!isRealUser(me)) {
    return (
      <button
        onClick={() => {
          window.location.href = "/api/auth/login";
        }}
        title="Sign in to save and organize diagrams"
        style={buttonStyle}
      >
        Sign in with Google
      </button>
    );
  }

  // Real Google user → avatar + first name opening a small menu.
  const user = me!.user!;
  const firstName =
    user.name?.split(" ")[0] || user.email?.split("@")[0] || "Account";

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        onClick={() => setMenuOpen((v) => !v)}
        title={user.email ?? undefined}
        style={{ ...buttonStyle, padding: "0 0.7rem" }}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            referrerPolicy="no-referrer"
            style={{ width: 22, height: 22, borderRadius: "50%" }}
          />
        ) : (
          <span
            aria-hidden
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--color-primary, #6965db)",
              color: "#fff",
              fontSize: 12,
            }}
          >
            {firstName[0]?.toUpperCase()}
          </span>
        )}
        {firstName}
      </button>
      {menuOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + .35rem)",
            right: 0,
            minWidth: 200,
            background: "var(--island-bg-color, #fff)",
            border: "1px solid var(--default-border-color, #e3e2e4)",
            borderRadius: 10,
            boxShadow: "0 6px 24px rgba(0,0,0,.12)",
            padding: ".25rem",
            zIndex: 10,
          }}
        >
          <div
            style={{
              padding: ".4rem .6rem",
              fontSize: 12,
              opacity: 0.6,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user.email}
          </div>
          <button
            onClick={signOut}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: ".5rem .6rem",
              borderRadius: 6,
              fontSize: 14,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                "var(--color-surface-high, #f1f0ff)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};
